// MSAL configuration shared by the React provider (`AuthProvider`) and the API
// client (`api/client.ts`). Both import `msalInstance` so token acquisition
// stays single-source-of-truth — a stray second `PublicClientApplication` would
// have its own cache and leak silent-acquire failures.
//
// The three Entra identifiers below are baked into source. None of them are
// secrets — the tenant id is public in every PKCE flow, the SPA client id
// shows up in the redirect URL during sign-in, and the API audience is the
// hub-api app reg's bare appId. They also don't change per environment: the
// hub is a single-deployment app pointed at one corp tenant. Treating them as
// build-time env vars (the previous approach) added a class of "blank-auth
// image" failure with no upside; if the app reg is ever recreated, the two
// places to update are this file (ENTRA_TENANT_ID / HUB_WEB_CLIENT_ID /
// HUB_API_AUDIENCE below) and the matching `Hub:Auth` block in
// `src/Hub.Api/appsettings.json`. `.pipelines/scripts/entra-bootstrap.sh`'s
// summary block prints the canonical values to copy.
//
// HUB_API_AUDIENCE is the BARE appId GUID (NOT `api://<appId>`) because
// hub-api's app reg uses `requestedAccessTokenVersion: 2` — v2 tokens carry
// the appId in `aud`, not the identifierUri. The matching backend audience
// lives in `src/Hub.Api/appsettings.json` under `Hub:Auth:Audience`; the two
// must stay equal.

import { LogLevel, PublicClientApplication, type Configuration } from '@azure/msal-browser';

const ENTRA_TENANT_ID = 'bcec3ebd-4f9a-4173-b803-7242f1d19061';
const HUB_WEB_CLIENT_ID = 'ef64c7e3-623e-4b95-a393-14ee29b7eb4b';
const HUB_API_AUDIENCE = 'f1508208-6ebc-4bef-808a-2edcfbe753d8';
const HUB_API_SCOPE = `api://${HUB_API_AUDIENCE}/access_as_user`;

const msalConfig: Configuration = {
  auth: {
    clientId: HUB_WEB_CLIENT_ID,
    authority: `https://login.microsoftonline.com/${ENTRA_TENANT_ID}`,
    // Same-origin redirect — hub.caredocs.ai/ in prod, http://localhost:5173/
    // in dev. Both are registered as SPA redirect URIs on the hub-web app reg
    // (see .pipelines/scripts/entra-bootstrap.sh). Reading window.location.origin
    // means we never have to bake the public hostname into the bundle.
    //
    // `navigateToLoginRequestUrl` is no longer a config-level option in
    // msal-browser v5 — it defaults to true, which is what we want: after
    // sign-in MSAL navigates back to the URL that initiated the redirect.
    redirectUri: `${window.location.origin}/`,
    postLogoutRedirectUri: `${window.location.origin}/`,
  },
  cache: {
    // sessionStorage keeps tokens in-tab — closing the browser signs you out,
    // which is the right default for an internal admin console. Switch to
    // 'localStorage' if SSO-across-tabs becomes a user request.
    cacheLocation: 'sessionStorage',
  },
  system: {
    // The hub is never embedded — refuse the redirect flow if someone tries.
    allowRedirectInIframe: false,
    loggerOptions: {
      // Quiet by default; flip to LogLevel.Verbose temporarily when debugging
      // a token-acquire failure.
      logLevel: LogLevel.Warning,
      piiLoggingEnabled: false,
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return;
        if (level === LogLevel.Error) console.error('[msal]', message);
        else if (level === LogLevel.Warning) console.warn('[msal]', message);
      },
    },
  },
};

/** Scopes requested on every API access-token acquire. The SCOPE is the full
 *  `api://<hub-api-appId>/access_as_user` URI (what we request); the resulting
 *  access token's `aud` claim is the BARE `<hub-api-appId>` GUID (what
 *  Hub.Api validates), because the hub-api app reg uses
 *  `requestedAccessTokenVersion: 2`. Hub.Api validates this via plain
 *  `Microsoft.AspNetCore.Authentication.JwtBearer` with
 *  `TokenValidationParameters.ValidateAudience = true` against
 *  `Hub:Auth:Audience` (the bare GUID, set in `src/Hub.Api/appsettings.json`).
 *  Mismatching the two forms is the classic v1/v2 audience-format gotcha. */
export const apiScopes: readonly string[] = Object.freeze([HUB_API_SCOPE]);

/** Singleton — import this everywhere instead of constructing a new one. */
export const msalInstance = new PublicClientApplication(msalConfig);
